import { fetchJSON as fetchAPI } from './client';

export type FinancialDashboard = {
  tenant_id: string;
  total_revenue: number;
  gross_margin_pct: number;
  net_margin_pct: number;
  cash_position: number;
  cash_runway_days: number;
  net_working_capital: number;
  financial_health_score: number;
  active_risks: number;
  active_recommendations: number;
  liquidity_risk?: string;
  generated_at?: string;
};

export type CashForecastRecord = {
  id: string;
  period: string;
  opening_balance: number;
  inflows: number;
  outflows: number;
  closing_balance: number;
  cash_runway_days: number;
  liquidity_risk?: string;
  generated_at?: string;
};

export type WorkingCapitalRecord = {
  id: string;
  inventory_carrying_cost: number;
  inventory_investment: number;
  supplier_payments_due: number;
  settlement_cycle_days: number;
  advertising_commitments: number;
  tax_obligations: number;
  operating_expenses: number;
  cash_conversion_cycle_days: number;
  net_working_capital: number;
  generated_at?: string;
};

export type ProfitabilityRecord = {
  id: string;
  dimension: string;
  dimension_value: string;
  revenue: number;
  gross_profit: number;
  net_profit: number;
  contribution_margin_pct: number;
  gross_margin_pct: number;
  net_margin_pct: number;
};

export type BusinessHealthRecord = {
  id: string;
  financial_health_score: number;
  liquidity_score: number;
  working_capital_score: number;
  profitability_score: number;
  growth_sustainability_score: number;
  operational_efficiency_score: number;
};

export type FinancialRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  business_context?: string;
  expected_impact?: string;
  cash_impact: number;
  profit_impact: number;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

export type ScenarioRecord = {
  id: string;
  type: string;
  title: string;
  description?: string;
  cash_impact: number;
  profit_impact: number;
  revenue_impact: number;
  liquidity_impact?: string;
  confidence_score: number;
  created_at?: string;
};

export type FinancialRiskRecord = {
  id: string;
  title: string;
  description?: string;
  category?: string;
  severity?: string;
  impact?: string;
  cash_at_risk: number;
  source_module?: string;
};

export type RunScenarioRequest = {
  type?: string;
  description?: string;
};

const API_BASE = import.meta.env.VITE_FININTEL_API ?? '/api/v1/finintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const finintelApi = {
  dashboard: () => fetchJSON<FinancialDashboard>('/dashboard'),
  listCashFlow: () => fetchJSON<CashForecastRecord[]>('/cash-flow'),
  listWorkingCapital: () => fetchJSON<WorkingCapitalRecord[]>('/working-capital'),
  listProfitability: () => fetchJSON<ProfitabilityRecord[]>('/profitability'),
  listHealthScores: () => fetchJSON<BusinessHealthRecord[]>('/health-scores'),
  listRecommendations: () => fetchJSON<FinancialRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<FinancialRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listScenarios: () => fetchJSON<ScenarioRecord[]>('/scenarios'),
  runScenario: (body: RunScenarioRequest) =>
    fetchJSON<ScenarioRecord>('/scenarios/run', { method: 'POST', body: JSON.stringify(body) }),
  listRisks: () => fetchJSON<FinancialRiskRecord[]>('/risks'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
